import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { adminApi } from '../services/adminApi';
import { PatientInquiry, FamilyMemberDietaryProfile } from '../types';
import { format } from 'date-fns';

const DIETITIANS = [
  { id: '12435969', name: 'Gretchen Benson' },
  { id: '11831244', name: 'Carrie Gonzalez' },
  { id: '13113158', name: 'Tracy Mindiola' },
  { id: '9943762', name: 'Laura Russell' },
  { id: '9598721', name: 'Brandon Morales (Admin)' },
  { id: '13091169', name: 'John Doe (Test)' },
];

export function PatientInquiryList({ canViewInsuranceCards = false }: { canViewInsuranceCards?: boolean }) {
  const queryClient = useQueryClient();
  const [selectedInquiry, setSelectedInquiry] = useState<PatientInquiry | null>(null);
  const [editedInquiry, setEditedInquiry] = useState<Partial<PatientInquiry>>({});
  const [selectedDietitianId, setSelectedDietitianId] = useState('');
  const [createAccountResult, setCreateAccountResult] = useState<{ success: boolean; message: string } | null>(null);
  const [eligibilityResult, setEligibilityResult] = useState<{
    status: 'active' | 'inactive' | 'unknown' | 'error';
    plan?: string;
    memberId?: string;
    groupNumber?: string;
    deductible?: string;
    copay?: string;
    message: string;
    checkedAt?: string;
  } | null>(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);

  const { data: inquiries, isLoading, error } = useQuery({
    queryKey: ['patientInquiries', canViewInsuranceCards],
    queryFn: () => adminApi.getPatientInquiries(canViewInsuranceCards),
    refetchInterval: 30000,
  });

  const updateMutation = useMutation({
    mutationFn: (inquiry: Partial<PatientInquiry>) => adminApi.updatePatientInquiry(inquiry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patientInquiries'] });
      setSelectedInquiry(null);
      setEditedInquiry({});
    },
  });

  const createAccountMutation = useMutation({
    mutationFn: ({ inquiry, dietitianId }: { inquiry: PatientInquiry; dietitianId: string }) =>
      adminApi.createHealthieAccount(inquiry, dietitianId),
    onSuccess: (data) => {
      const healthieId = data.healthie_patient?.id || 'created';
      const now = new Date().toISOString();
      if (selectedInquiry) {
        setSelectedInquiry({ ...selectedInquiry, healthie_patient_id: healthieId, account_created_at: now });
        adminApi.updatePatientInquiry({
          id: selectedInquiry.id,
          status: 'activated',
          healthie_patient_id: healthieId,
          account_created_at: now,
        }).catch((err) => console.error('Failed to persist activation:', err));
      }
      queryClient.invalidateQueries({ queryKey: ['patientInquiries'] });
      setCreateAccountResult({
        success: true,
        message: `Account activated (Healthie ID: ${healthieId}). Password setup email sent to ${selectedInquiry?.email}.`,
      });
    },
    onError: (err: any) => {
      const errData = err?.response?.data;
      let detail = '';
      if (typeof errData === 'string') {
        detail = errData;
      } else if (errData?.error) {
        detail = typeof errData.error === 'string' ? errData.error : JSON.stringify(errData.error);
      } else if (err?.message) {
        detail = err.message;
      } else {
        detail = JSON.stringify(err);
      }
      setCreateAccountResult({
        success: false,
        message: `Failed to create account: ${detail}`,
      });
    },
  });

  const handleEdit = (inquiry: PatientInquiry) => {
    setSelectedInquiry(inquiry);
    setEditedInquiry({
      id: inquiry.id,
      status: inquiry.status,
      assigned_dietitian_id: inquiry.assigned_dietitian_id || '',
      notes: inquiry.notes || '',
    });
    setSelectedDietitianId(inquiry.assigned_dietitian_id || '');
    setCreateAccountResult(null);
    setEligibilityResult(null);
    createAccountMutation.reset();
  };

  const handleSave = () => {
    if (editedInquiry.id) {
      updateMutation.mutate(editedInquiry);
    }
  };

  const handleCreateAccount = () => {
    if (!selectedInquiry || !selectedDietitianId) return;
    if (!selectedInquiry.email) {
      setCreateAccountResult({ success: false, message: 'Patient email is required to create a Healthie account.' });
      return;
    }
    const nameParts = selectedInquiry.full_name.trim().split(/\s+/);
    const parsedFirst = nameParts[0] || '';
    const parsedLast = nameParts.slice(1).join(' ') || '';
    const dietitianName = DIETITIANS.find(d => d.id === selectedDietitianId)?.name || 'selected dietitian';
    if (!window.confirm(
      `Create Healthie account and send password email?\n\n` +
      `Name: ${selectedInquiry.full_name}\n` +
      `  → First: "${parsedFirst}"\n` +
      `  → Last: "${parsedLast}"\n` +
      `Email: ${selectedInquiry.email}\n` +
      `Dietitian: ${dietitianName}\n\n` +
      `⚠️ This cannot be undone. Verify the name looks correct.`
    )) return;
    createAccountMutation.mutate({ inquiry: selectedInquiry, dietitianId: selectedDietitianId });
  };

  const handleEligibilityCheck = async () => {
    if (!selectedInquiry) return;
    setEligibilityLoading(true);
    setEligibilityResult(null);
    try {
      const azureBase = process.env.REACT_APP_AZURE_FN_URL || 'https://famelia-healthie-auth-bridge.azurewebsites.net';
      const resp = await fetch(`${azureBase}/api/checkEligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: selectedInquiry.full_name,
          insurance_plan: selectedInquiry.insurance_plan,
          insurance_member_id: selectedInquiry.insurance_member_id || '',
          healthie_patient_id: selectedInquiry.healthie_patient_id || undefined,
          dob: selectedInquiry.dob || undefined,
          state: selectedInquiry.state || undefined,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setEligibilityResult({
        status: data.eligible === true ? 'active' : data.eligible === false ? 'inactive' : 'unknown',
        plan: data.plan_name || selectedInquiry.insurance_plan,
        memberId: data.member_id || selectedInquiry.insurance_member_id,
        groupNumber: data.group_number,
        deductible: data.deductible,
        copay: data.copay,
        message: data.message || (data.eligible ? 'Coverage verified and active.' : 'Coverage not confirmed — may need manual verification.'),
        checkedAt: new Date().toLocaleString(),
      });
    } catch (err: any) {
      // Endpoint not yet live — show a clear placeholder
      setEligibilityResult({
        status: 'error',
        message: err?.message?.includes('404') || err?.message?.includes('HTTP 4')
          ? 'Eligibility endpoint not yet deployed. Build /api/checkEligibility in the Azure bridge to enable this.'
          : `Check failed: ${err.message}`,
        checkedAt: new Date().toLocaleString(),
      });
    } finally {
      setEligibilityLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'contacted': return 'info';
      case 'matched': return 'primary';
      case 'activated': return 'success';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'matched': return 'Dietitian Assigned';
      case 'activated': return 'Account Activated';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Helper to get a summary of dietary tags
  const getDietarySummary = (profile: FamilyMemberDietaryProfile[] | null) => {
    if (!profile || profile.length === 0) return null;
    
    const allRestrictions = new Set<string>();
    const allAllergens = new Set<string>();
    const allConditions = new Set<string>();
    
    profile.forEach(member => {
      member.dietaryRestrictions?.forEach(r => allRestrictions.add(r));
      member.allergens?.forEach(a => allAllergens.add(a));
      member.medicalConditions?.forEach(c => allConditions.add(c));
    });
    
    const totalTags = allRestrictions.size + allAllergens.size + allConditions.size;
    
    return {
      restrictions: Array.from(allRestrictions),
      allergens: Array.from(allAllergens),
      conditions: Array.from(allConditions),
      totalTags,
      memberCount: profile.length
    };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load patient inquiries: {String(error)}</Alert>;
  }

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Patient Sign-Up Inquiries ({inquiries?.length || 0})
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage patient portal sign-up requests and assign dietitians
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Submitted</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Family</TableCell>
              <TableCell>Diet Profile</TableCell>
              <TableCell>Insurance</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Dietitian</TableCell>
              <TableCell>Healthie</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inquiries?.map((inquiry) => (
              <TableRow key={inquiry.id} hover>
                <TableCell>
                  {format(new Date(inquiry.submitted_at), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <strong>{inquiry.full_name}</strong>
                </TableCell>
                <TableCell>
                  <Box>
                    {inquiry.email && <div>{inquiry.email}</div>}
                    {inquiry.phone && <div>{inquiry.phone}</div>}
                  </Box>
                </TableCell>
                <TableCell>
                  {inquiry.family_size} ({inquiry.family_relation})
                </TableCell>
                <TableCell>
                  {(() => {
                    const summary = getDietarySummary(inquiry.dietary_profile);
                    if (!summary) return <Typography variant="body2" color="text.secondary">-</Typography>;
                    
                    return (
                      <Tooltip 
                        title={
                          <Box sx={{ p: 1 }}>
                            {summary.restrictions.length > 0 && (
                              <Box mb={0.5}>
                                <Typography variant="caption" fontWeight="bold">🥗 Restrictions:</Typography>
                                <Typography variant="caption" display="block">{summary.restrictions.join(', ')}</Typography>
                              </Box>
                            )}
                            {summary.allergens.length > 0 && (
                              <Box mb={0.5}>
                                <Typography variant="caption" fontWeight="bold">⚠️ Allergens:</Typography>
                                <Typography variant="caption" display="block">{summary.allergens.join(', ')}</Typography>
                              </Box>
                            )}
                            {summary.conditions.length > 0 && (
                              <Box>
                                <Typography variant="caption" fontWeight="bold">🏥 Conditions:</Typography>
                                <Typography variant="caption" display="block">{summary.conditions.join(', ')}</Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip 
                            label={`${summary.totalTags} tags`} 
                            size="small" 
                            color={summary.totalTags > 0 ? 'primary' : 'default'}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {summary.memberCount} member{summary.memberCount > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <span>{inquiry.insurance_plan}</span>
                    {inquiry.insurance_member_id && (
                      <Chip label={`ID ${inquiry.insurance_member_id}`} size="small" variant="outlined" />
                    )}
                    {inquiry.has_insurance_cards && (
                      <Chip label="Card photos" size="small" color="info" variant="outlined" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{inquiry.state}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(inquiry.status)}
                    color={getStatusColor(inquiry.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {inquiry.assigned_dietitian_id
                    ? DIETITIANS.find(d => d.id === inquiry.assigned_dietitian_id)?.name || inquiry.assigned_dietitian_id
                    : '-'}
                </TableCell>
                <TableCell>
                  {inquiry.healthie_patient_id ? (
                    <Chip label={`ID: ${inquiry.healthie_patient_id}`} size="small" color="success" />
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleEdit(inquiry)}
                  >
                    Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!selectedInquiry}
        onClose={() => { setSelectedInquiry(null); setCreateAccountResult(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Patient Inquiry</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Patient: {selectedInquiry?.full_name}
              {selectedInquiry?.source && (
                <Chip label={selectedInquiry.source} size="small" sx={{ ml: 1 }} />
              )}
            </Typography>

            {/* Health Profile Section */}
            {(selectedInquiry?.dob || selectedInquiry?.gender || selectedInquiry?.height_inches || selectedInquiry?.weight_lbs || selectedInquiry?.goals_detail) && (
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Health Profile</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {selectedInquiry?.dob && (
                      <Typography variant="body2"><strong>DOB:</strong> {selectedInquiry.dob}</Typography>
                    )}
                    {selectedInquiry?.gender && (
                      <Typography variant="body2"><strong>Gender:</strong> {selectedInquiry.gender}</Typography>
                    )}
                    {selectedInquiry?.height_inches && (
                      <Typography variant="body2"><strong>Height:</strong> {selectedInquiry.height_inches} in</Typography>
                    )}
                    {selectedInquiry?.weight_lbs && (
                      <Typography variant="body2"><strong>Weight:</strong> {selectedInquiry.weight_lbs} lbs</Typography>
                    )}
                  </Box>
                  {selectedInquiry?.goals_detail && (
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Goals:</strong> {selectedInquiry.goals_detail}</Typography>
                  )}
                  {selectedInquiry?.medical_conditions && selectedInquiry.medical_conditions.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ width: '100%' }}><strong>Conditions:</strong></Typography>
                      {selectedInquiry.medical_conditions.map((c, i) => (
                        <Chip key={i} label={c} size="small" color="warning" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Consents Section */}
            {selectedInquiry?.consents && (
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Consents</Typography>
                  {selectedInquiry.consents.hipaa && selectedInquiry.consents.telehealth &&
                   selectedInquiry.consents.nutrition_services && (
                    <Chip label="Required consents signed" size="small" color="success" sx={{ ml: 1 }} />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      { key: 'hipaa', label: 'HIPAA Agreement' },
                      { key: 'telehealth', label: 'Telehealth Consent' },
                      { key: 'nutrition_services', label: 'Nutrition Services' },
                    ].map(({ key, label }) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(selectedInquiry.consents as any)[key] ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                        <Typography variant="body2">{label}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {selectedInquiry.consents.financial_responsibility ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <Chip label="Later" size="small" variant="outlined" />
                      )}
                      <Typography variant="body2">
                        Financial Responsibility
                        {!selectedInquiry.consents.financial_responsibility
                          ? ' — collected after coverage check and dietitian assignment'
                          : ''}
                      </Typography>
                    </Box>
                    {selectedInquiry.consents.signature && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Signed: "{selectedInquiry.consents.signature}" on {format(new Date(selectedInquiry.consents.consented_at), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Dietary Profile Section */}
            {selectedInquiry?.dietary_profile && selectedInquiry.dietary_profile.length > 0 && (
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">
                    Family Diet Profile ({selectedInquiry.dietary_profile.length} member{selectedInquiry.dietary_profile.length > 1 ? 's' : ''})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedInquiry.dietary_profile.map((member, idx) => (
                    <Box key={idx} sx={{ mb: idx < selectedInquiry.dietary_profile!.length - 1 ? 2 : 0 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {member.name} {member.isCurrentUser && <Chip label="Inquirer" size="small" color="info" sx={{ ml: 1 }} />}
                      </Typography>
                      
                      {member.dietaryRestrictions?.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Restrictions:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {member.dietaryRestrictions.map((tag, i) => (
                              <Chip key={i} label={tag} size="small" color="success" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {member.allergens?.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Allergens:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {member.allergens.map((tag, i) => (
                              <Chip key={i} label={tag} size="small" color="error" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {member.medicalConditions?.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Conditions:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {member.medicalConditions.map((tag, i) => (
                              <Chip key={i} label={tag} size="small" color="warning" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {idx < selectedInquiry.dietary_profile!.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            <Divider sx={{ my: 1 }} />

            {(selectedInquiry?.insurance_member_id ||
              selectedInquiry?.insurance_group_number ||
              selectedInquiry?.secondary_insurance_plan ||
              selectedInquiry?.request_oon_estimate ||
              selectedInquiry?.has_insurance_cards ||
              selectedInquiry?.insurance_ocr) && (
              <Accordion defaultExpanded sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Insurance details</Typography>
                  {selectedInquiry.has_insurance_cards && (
                    <Chip label="Photos on file" size="small" color="info" sx={{ ml: 1 }} />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {selectedInquiry.insurance_member_id && (
                      <Chip label={`Member ID: ${selectedInquiry.insurance_member_id}`} size="small" color="info" />
                    )}
                    {selectedInquiry.insurance_group_number && (
                      <Chip label={`Group: ${selectedInquiry.insurance_group_number}`} size="small" />
                    )}
                    {selectedInquiry.secondary_insurance_plan && (
                      <Chip label={`Secondary: ${selectedInquiry.secondary_insurance_plan}`} size="small" variant="outlined" />
                    )}
                    {selectedInquiry.secondary_insurance_member_id && (
                      <Chip label={`Secondary ID: ${selectedInquiry.secondary_insurance_member_id}`} size="small" variant="outlined" />
                    )}
                    {selectedInquiry.request_oon_estimate && (
                      <Chip label="OON estimate requested" size="small" color="warning" />
                    )}
                  </Box>

                  {canViewInsuranceCards && (
                    selectedInquiry.insurance_card_attempts?.length ||
                    selectedInquiry.insurance_card_front_url ||
                    selectedInquiry.insurance_card_back_url ||
                    selectedInquiry.secondary_card_front_url ||
                    selectedInquiry.secondary_card_back_url
                  ) ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Card photos — every upload attempt (keep until entered in Healthie)
                      </Typography>
                      {(['primary', 'secondary'] as const).map((card) => {
                        const attempts = (selectedInquiry.insurance_card_attempts || [])
                          .filter((a) => a.card === card && a.url)
                          .sort((a, b) => String(a.uploaded_at || a.path).localeCompare(String(b.uploaded_at || b.path)));
                        const legacy = card === 'primary'
                          ? [
                              selectedInquiry.insurance_card_front_url && { side: 'front' as const, url: selectedInquiry.insurance_card_front_url, uploaded_at: undefined },
                              selectedInquiry.insurance_card_back_url && { side: 'back' as const, url: selectedInquiry.insurance_card_back_url, uploaded_at: undefined },
                            ].filter(Boolean) as Array<{ side: 'front' | 'back'; url: string; uploaded_at?: string }>
                          : [
                              selectedInquiry.secondary_card_front_url && { side: 'front' as const, url: selectedInquiry.secondary_card_front_url, uploaded_at: undefined },
                              selectedInquiry.secondary_card_back_url && { side: 'back' as const, url: selectedInquiry.secondary_card_back_url, uploaded_at: undefined },
                            ].filter(Boolean) as Array<{ side: 'front' | 'back'; url: string; uploaded_at?: string }>;
                        const shown = attempts.length ? attempts : legacy.map((item) => ({ ...item, card, path: '' }));
                        if (!shown.length) return null;
                        const attemptNo: Record<string, number> = {};
                        return (
                          <Box key={card} sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>{card} card</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              {shown.map((attempt, idx) => {
                                const n = (attemptNo[attempt.side] = (attemptNo[attempt.side] || 0) + 1);
                                const sideCount = shown.filter((a) => a.side === attempt.side).length;
                                return (
                                  <Box key={`${attempt.side}-${attempt.uploaded_at || attempt.path || idx}`}>
                                    <Typography variant="caption" display="block">
                                      {attempt.side === 'back' ? 'Back' : 'Front'}
                                      {sideCount > 1 ? ` · attempt ${n}` : ''}
                                      {attempt.uploaded_at ? ` · ${format(new Date(attempt.uploaded_at), 'MMM d, h:mm a')}` : ''}
                                    </Typography>
                                    <Box
                                      component="a"
                                      href={attempt.url || undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Box
                                        component="img"
                                        src={attempt.url || ''}
                                        alt={`${card} insurance card ${attempt.side} attempt ${n}`}
                                        sx={{ display: 'block', width: 240, maxWidth: '100%', borderRadius: 1, border: '1px solid #eee' }}
                                      />
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : selectedInquiry.has_insurance_cards && !canViewInsuranceCards ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Insurance card photos are on file. Only dietitians and the main admin can view them.
                    </Typography>
                  ) : null}

                  {(() => {
                    const ocr = selectedInquiry.insurance_ocr;
                    const fields = [
                      ocr?.primary?.front,
                      ocr?.primary?.back,
                      ocr?.secondary?.front,
                      ocr?.secondary?.back,
                    ];
                    const extras = new Set<string>();
                    fields.forEach((f) => {
                      if (f?.subscriber_name) extras.add(`Name: ${f.subscriber_name}`);
                      if (f?.payer_name) extras.add(`Payer: ${f.payer_name}`);
                      if (f?.plan_type) extras.add(`Plan type: ${f.plan_type}`);
                      if (f?.rx_bin) extras.add(`Rx BIN: ${f.rx_bin}`);
                      if (f?.rx_pcn) extras.add(`Rx PCN: ${f.rx_pcn}`);
                      if (f?.rx_grp) extras.add(`Rx GRP: ${f.rx_grp}`);
                      if (f?.payer_id) extras.add(`Payer ID: ${f.payer_id}`);
                    });
                    if (!extras.size) return null;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.from(extras).map((label) => (
                          <Chip key={label} label={label} size="small" variant="outlined" />
                        ))}
                      </Box>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            )}

            <TextField
              select
              fullWidth
              label="Status"
              value={editedInquiry.status || 'pending'}
              onChange={(e) => setEditedInquiry({ ...editedInquiry, status: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="contacted">Contacted</MenuItem>
              <MenuItem value="matched">Dietitian Assigned</MenuItem>
              <MenuItem value="activated">Account Activated</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Assign Dietitian"
              value={selectedDietitianId}
              onChange={(e) => {
                const dietitianId = e.target.value;
                setSelectedDietitianId(dietitianId);
                const updates: any = { ...editedInquiry, assigned_dietitian_id: dietitianId };
                if (dietitianId && editedInquiry.status === 'pending') {
                  updates.status = 'matched';
                }
                setEditedInquiry(updates);
              }}
            >
              <MenuItem value="">-- Select Dietitian --</MenuItem>
              {DIETITIANS.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Admin Notes"
              value={editedInquiry.notes || ''}
              onChange={(e) => setEditedInquiry({ ...editedInquiry, notes: e.target.value })}
              placeholder="Add any internal notes about this inquiry..."
            />

            {/* Insurance Eligibility Check */}
            <Box sx={{ p: 2, border: '1.5px solid #E3F2FD', borderRadius: 2, bgcolor: '#fafcff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VerifiedUserIcon fontSize="small" sx={{ color: '#1976d2' }} />
                <Typography variant="subtitle2" fontWeight="bold">Insurance Eligibility Check</Typography>
                {selectedInquiry?.insurance_plan && (
                  <Chip label={selectedInquiry.insurance_plan} size="small" variant="outlined" />
                )}
                {selectedInquiry?.insurance_member_id && (
                  <Chip label={`ID: ${selectedInquiry.insurance_member_id}`} size="small" color="info" variant="outlined" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Runs a real-time check against Healthie's eligibility system using the patient's plan and member ID.
                {!selectedInquiry?.insurance_member_id && (
                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 500 }}>
                    {' '}No member ID on file — check may be less accurate.
                  </Box>
                )}
              </Typography>

              {eligibilityLoading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

              {eligibilityResult && (
                <Alert
                  severity={
                    eligibilityResult.status === 'active' ? 'success'
                    : eligibilityResult.status === 'inactive' ? 'warning'
                    : eligibilityResult.status === 'error' ? 'error'
                    : 'info'
                  }
                  sx={{ mb: 1.5, fontSize: 13 }}
                >
                  <strong>
                    {eligibilityResult.status === 'active' ? 'Active Coverage'
                      : eligibilityResult.status === 'inactive' ? 'Not Covered / Inactive'
                      : eligibilityResult.status === 'error' ? 'Check Failed'
                      : 'Unknown / Needs Review'}
                  </strong>
                  {' '}&mdash; {eligibilityResult.message}
                  {(eligibilityResult.deductible || eligibilityResult.copay || eligibilityResult.groupNumber) && (
                    <Box sx={{ mt: 0.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {eligibilityResult.groupNumber && <span><strong>Group:</strong> {eligibilityResult.groupNumber}</span>}
                      {eligibilityResult.deductible && <span><strong>Deductible:</strong> {eligibilityResult.deductible}</span>}
                      {eligibilityResult.copay && <span><strong>Copay:</strong> {eligibilityResult.copay}</span>}
                    </Box>
                  )}
                  {eligibilityResult.checkedAt && (
                    <Box sx={{ mt: 0.5, fontSize: 11, color: 'text.secondary' }}>Checked {eligibilityResult.checkedAt}</Box>
                  )}
                </Alert>
              )}

              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={eligibilityLoading ? <CircularProgress size={14} /> : <VerifiedUserIcon />}
                onClick={handleEligibilityCheck}
                disabled={eligibilityLoading || !selectedInquiry?.insurance_plan}
                fullWidth
              >
                {eligibilityLoading ? 'Checking...' : eligibilityResult ? 'Re-check Eligibility' : 'Run Eligibility Check'}
              </Button>
              {!selectedInquiry?.insurance_plan && (
                <Typography variant="caption" color="error">Insurance plan is required to run a check</Typography>
              )}
            </Box>

            {/* Healthie Account Creation */}
            {selectedInquiry?.healthie_patient_id ? (
              <Alert severity="success">
                Healthie account already created (ID: {selectedInquiry.healthie_patient_id})
                {selectedInquiry.account_created_at && (
                  <> on {format(new Date(selectedInquiry.account_created_at), 'MMM d, yyyy h:mm a')}</>
                )}
              </Alert>
            ) : (
              <Box sx={{ p: 2, border: '2px dashed #4CAF50', borderRadius: 2, bgcolor: '#f8fdf8' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Activate Patient Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This will create the patient's Healthie account, submit their signed consent forms, assign their dietitian, and send them an email to set their password. They will only need to fill out the Insurance Form.
                </Typography>
                {!selectedInquiry?.consents && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    No consent data found. The patient should complete consent forms before account creation.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCreateAccount}
                  disabled={!selectedDietitianId || !selectedInquiry?.email || createAccountMutation.isPending || createAccountMutation.isSuccess}
                  fullWidth
                >
                  {createAccountMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : createAccountMutation.isSuccess ? (
                    '✓ Account Created Successfully'
                  ) : (
                    `Activate Account & Send Invite${selectedDietitianId ? ` (${DIETITIANS.find(d => d.id === selectedDietitianId)?.name})` : ''}`
                  )}
                </Button>
                {!selectedDietitianId && (
                  <Typography variant="caption" color="error">Select a dietitian above first</Typography>
                )}
                {!selectedInquiry?.email && (
                  <Typography variant="caption" color="error">Patient email is required</Typography>
                )}
              </Box>
            )}

            {createAccountResult && (
              <Alert severity={createAccountResult.success ? 'success' : 'error'}>
                {createAccountResult.message}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelectedInquiry(null); setCreateAccountResult(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
